import { useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import useAppSettingsStore from "@/store/appSettingsStore";
import { useAppLockStore } from "@/store/appLockStore";
import PatternLock from "@/components/app-lock/PatternLock";

function lockoutSeconds(lockoutUntil, now) {
  if (!lockoutUntil || lockoutUntil <= now) return 0;
  return Math.ceil((lockoutUntil - now) / 1000);
}

export default function AppLockOverlay() {
  const { logout } = useAuth();
  const appName = useAppSettingsStore((state) => state.settings.app_name);
  const unlock = useAppLockStore((state) => state.unlock);
  const forgetActivePattern = useAppLockStore((state) => state.forgetActivePattern);
  const lockoutUntil = useAppLockStore((state) => state.lockoutUntil);
  const [now, setNow] = useState(() => Date.now());
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [resetSignal, setResetSignal] = useState(0);
  const [confirmForget, setConfirmForget] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const panelRef = useRef(null);

  const secondsLeft = lockoutSeconds(lockoutUntil, now);
  const lockedOut = secondsLeft > 0;

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    if (!lockedOut) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [lockedOut]);

  async function handleComplete(pattern) {
    const result = await unlock(pattern);
    if (result.ok) return;
    setStatus("error");
    setResetSignal((value) => value + 1);
    if (result.reason === "lockout") {
      setNow(Date.now());
      setMessage("تلاش‌ها تمام شد. چند لحظه بعد دوباره الگو را بکشید.");
    } else {
      const remaining = result.remaining ?? 0;
      setMessage(remaining > 0 ? `الگو اشتباه است. ${remaining} تلاش باقی مانده.` : "الگو اشتباه است.");
    }
    window.setTimeout(() => setStatus("idle"), 700);
  }

  async function handleForget() {
    setLeaving(true);
    forgetActivePattern({ keepLocked: true });
    try {
      await logout();
    } finally {
      setLeaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="قفل برنامه"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-background px-6"
    >
      <div ref={panelRef} tabIndex={-1} className="flex w-full max-w-sm flex-col items-center gap-6 text-center outline-none">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Lock size={26} />
        </div>
        <div className="space-y-1">
          {appName ? <p className="text-sm text-muted-foreground">{appName}</p> : null}
          <h1 className="text-xl font-semibold">برنامه قفل شد</h1>
          <p className="text-sm text-muted-foreground">برای ادامه، الگوی خود را بکشید.</p>
        </div>

        <PatternLock
          disabled={lockedOut || leaving}
          status={status}
          resetSignal={resetSignal}
          onComplete={handleComplete}
          onIncomplete={() => {
            setStatus("error");
            setMessage("حداقل ۴ نقطه لازم است.");
            setResetSignal((value) => value + 1);
            window.setTimeout(() => setStatus("idle"), 700);
          }}
        />

        <p className="min-h-5 text-sm text-destructive" aria-live="polite">
          {lockedOut ? `${secondsLeft} ثانیه تا تلاش دوباره` : message}
        </p>

        {confirmForget ? (
          <div className="w-full space-y-2">
            <p className="text-xs text-muted-foreground">
              الگوی این دستگاه حذف می‌شود. ورود بعدی با رمز عبور است.
            </p>
            <Button type="button" variant="destructive" className="w-full" disabled={leaving} onClick={handleForget}>
              {leaving ? "در حال خروج..." : "خروج و حذف الگو"}
            </Button>
            <Button type="button" variant="ghost" className="w-full" disabled={leaving} onClick={() => setConfirmForget(false)}>
              انصراف
            </Button>
          </div>
        ) : (
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            onClick={() => setConfirmForget(true)}
          >
            الگو را فراموش کرده‌ام
          </button>
        )}
      </div>
    </div>
  );
}
